import {
   JPBReferenceProperty,
   AreaUnit,
   AreaGrouping,
   DEFAULT_ASSUMPTIONS,
   YearGrouping,
   PriceMode,
   JPBPriceIndexSource,
   type JPBAssumptions
} from "./JPBState";
import {
   calculateEffectiveMarkSquareMeterPrice,
   formatArea,
   formatDate,
   formatPercentage,
   formatPricePerSquareMeter,
   getResolvedJPBAssumptions
} from "./JPBFunc";
import type {
   JPBDynamicBucketRule,
   JPBHovedstadenGroupingMode,
   JPBIndexComputationMethod,
   JPBIndexInterval,
   JPBIndexSmoothingMode,
   JPBIndexStatistic
} from "./JPBIndexBuilderTypes";

import JPBRegions from "./JPBRegions.json";
import JPBIndexData from "./JPBIndexOfLandPrices.json";
import { Localization } from "@viamap/viamap2-common";
import { SettingsManager } from "@viamap/viamap2-common/dist/managers/SettingsManager";
import { ESInterfaceJWTAuth } from "src/compentsModus/ESInterfaceJWTAuth";
import { combine, featureCollection, union } from "@turf/turf";

export type JPBReferenceTransactionElement = {
   geometry: any;
   centroid: any;
   date: Date;
   pricePerSquareMeter: number;
   areaInSquareMeter: number;
   totalPrice: number;
   transactionType: string;
   priceTodayPerSquareMeter: number;
   priceTodayTotal: number;
}

export type JPBQueryOptions = {
   dateFrom?: string;
   landbrugsnotering?: string;
   polygonCoordinates?: number[][][];
   size?: number;
   yearGrouping?: YearGrouping;
   calculationDateTo?: Date;
   priceIndexSource?: JPBPriceIndexSource;
}

export type JPBDatabaseMetadata = {
   dataset: string;
   targetIndex: string;
   uploadBatchId: string;
   uploadedDocs: number;
   uploadedAt: number;
   esDocCountAfter: number;
}

export type JPBDateBounds = {
   minDate: Date;
   maxDate: Date;
}

export type JPBIndexedLandsdelPeriod = {
   periodKey: string;
   periodStart: number;
   periodEnd: number;
   value: number | null;
   factor: number | null;
   txCount: number;
   dynamicBucketRule?: JPBDynamicBucketRule;
   dynamicHalfWindowQuarters?: number;
   dynamicRelativeCi95?: number | null;
   shrinkageWeight?: number | null;
   shrinkageRawValue?: number | null;
   shrinkagePriorValue?: number | null;
   fitFactor: number | null;
   fitValue: number | null;
}

export type JPBIndexedLandsdelFit = {
   a: number;
   b: number;
   rSquared: number;
   rmse: number | null;
}

export type JPBIndexedLandsdelDocument = {
   landsdel: string;
   computationMethod?: JPBIndexComputationMethod;
   interval: JPBIndexInterval;
   statistic: JPBIndexStatistic;
   smoothingMode: JPBIndexSmoothingMode;
   dynamicBucketGeneralization?: boolean;
   hovedstadenGroupingMode?: JPBHovedstadenGroupingMode;
   buildingAreaThresholdHa: number;
   pricePerM2Min?: number | null;
   pricePerM2Max?: number | null;
   subtractBuildingPrices: boolean;
   yearsBack: number;
   generatedAt: number;
   basePeriod: string | null;
   periods: JPBIndexedLandsdelPeriod[];
   fitExp?: JPBIndexedLandsdelFit;
}

export type JPBIndexedLandsdelState = {
   indexName: string;
   fetchedAt: number;
   documents: JPBIndexedLandsdelDocument[];
   landsdele: string[];
   byLandsdel: { [landsdel: string]: JPBIndexedLandsdelDocument[] };
   byLookupKey: { [lookupKey: string]: JPBIndexedLandsdelDocument };
}

export type JPBPriceIndexLandsdelFactorRow = {
   landsdel: string;
   avgTxCount: number | null;
   usesFittedModel: boolean;
   factorsByPeriod: { [periodKey: string]: number | null };
}

export type JPBPriceIndexFactorsTable = {
   source: JPBPriceIndexSource;
   periodKeys: string[];
   rows: JPBPriceIndexLandsdelFactorRow[];
}

const DEFAULT_AGRO_REGION_NAME = "Landsdel Vest- og Sydsjælland";
const MOLBAK_LOW_SAMPLE_AVG_TX_THRESHOLD = 30;
const HOVEDSTADEN_LANDSDEL = "Hovedstaden";

const LANDSDEL_TO_REGION_MAP: { [key: string]: string } = {
   "Hovedstaden": DEFAULT_AGRO_REGION_NAME,
   "Nordsjælland": DEFAULT_AGRO_REGION_NAME,
   "Østsjælland": DEFAULT_AGRO_REGION_NAME,
   "Vestsjælland": DEFAULT_AGRO_REGION_NAME,
   "Vest- og Sydsjælland": DEFAULT_AGRO_REGION_NAME,
   "Sjælland": DEFAULT_AGRO_REGION_NAME,
   "Fyn": "Landsdel Fyn",
   "Sydjylland": "Landsdel Sydjylland",
   "Østjylland": "Landsdel Østjylland",
   "Vestjylland": "Landsdel Vestjylland",
   "Nordjylland": "Landsdel Nordjylland",
   "Bornholm": DEFAULT_AGRO_REGION_NAME,
   "Lolland-Falster": DEFAULT_AGRO_REGION_NAME
};

const LANDSDEL_NR_TO_REGION_MAP: { [key: number]: string } = {
   1: DEFAULT_AGRO_REGION_NAME,
   2: DEFAULT_AGRO_REGION_NAME,
   3: DEFAULT_AGRO_REGION_NAME,
   4: DEFAULT_AGRO_REGION_NAME,
   5: DEFAULT_AGRO_REGION_NAME,
   6: DEFAULT_AGRO_REGION_NAME,
   7: "Landsdel Fyn",
   8: "Landsdel Sydjylland",
   9: "Landsdel Østjylland",
   10: "Landsdel Vestjylland",
   11: "Landsdel Nordjylland"
};

export class JPBManager {
   private static ESInterfaceJWTAuth: ESInterfaceJWTAuth | null = null;
   private static currentIndexName: string | null = null;
   private static databaseMetadata: JPBDatabaseMetadata | null = null;
   private static metadataLastFetched: number | null = null;
   private static dateBoundsCache: { indexName: string; minDateMs: number; maxDateMs: number; fetchedAt: number } | null = null;
   private static readonly dateBoundsCacheTimeoutMs: number = 5 * 60 * 1000;
   private static indexedLandsdelStateCache: JPBIndexedLandsdelState | null = null;
   private static indexedLandsdelStatePromise: Promise<JPBIndexedLandsdelState | null> | null = null;
   private static indexedLandsdelStateLastFetched: number | null = null;
   private static readonly indexedLandsdelStateCacheTimeoutMs: number = 5 * 60 * 1000;

   private static toEpochMilliseconds(value: unknown): number {
      const numericValue = typeof value === "number" ? value : Number(value);
      if (!Number.isFinite(numericValue) || numericValue <= 0) {
         return 0;
      }
      if (numericValue < 10_000_000_000) {
         return Math.floor(numericValue * 1000);
      }
      return Math.floor(numericValue);
   }

   private static getESInterfaceJWTAuth(): ESInterfaceJWTAuth {
      const indexName = SettingsManager.getSystemSetting("jpb.indexName", "jpb_landbrugshandler_testv5");

      // Recreate if index name changed or doesn't exist
      if (!JPBManager.ESInterfaceJWTAuth || JPBManager.currentIndexName !== indexName) {
         JPBManager.ESInterfaceJWTAuth = new ESInterfaceJWTAuth(indexName);
         JPBManager.currentIndexName = indexName;
      }
      return JPBManager.ESInterfaceJWTAuth;
   }

   private static getIndexedLandsdelIndexName(): string {
      return SettingsManager.getSystemSetting("jpb.indexBuilderIndex", "jpbindex").toLowerCase();
   }

   private static toFiniteNumber(value: unknown): number | null {
      const numericValue = typeof value === "number" ? value : Number(value);
      return Number.isFinite(numericValue) ? numericValue : null;
   }

   private static toStringOrNull(value: unknown): string | null {
      return typeof value === "string" && value.length > 0 ? value : null;
   }

   private static normalizeHovedstadenGroupingMode(value: unknown): JPBHovedstadenGroupingMode | undefined {
      if (
         value === "none" ||
         value === "withNordsjaelland" ||
         value === "withNordsjaellandAndOestsjaelland"
      ) {
         return value;
      }
      return undefined;
   }

   private static legacyDynamicRuleToHalfWindow(rule?: string): number | undefined {
      if (rule === "quarterRolling") return 1;
      if (rule === "halfYearRolling") return 3;
      if (rule === "fullYearRolling") return 6;
      return undefined;
   }

   private static normalizeStatusToken(value: unknown): string {
      if (typeof value !== "string") return "";
      return value.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
   }

   public static isSuccessfulTransactionStatus(status: unknown): boolean {
      const normalized = this.normalizeStatusToken(status);
      if (!normalized) return true;
      return [
         "ok",
         "success",
         "successful",
         "succeeded",
         "complete",
         "completed",
         "done",
         "matched",
         "ready",
         "processed",
         "uploaded",
         "valid"
      ].includes(normalized);
   }

   public static getTransactionIssueText(
      transactionStatus: unknown,
      reasonText: unknown,
      reasonCode: unknown
   ): string | null {
      const statusText = this.toStringOrNull(transactionStatus)?.trim() || null;
      const reasonTextValue = this.toStringOrNull(reasonText)?.trim() || null;
      const reasonCodeValue = this.toStringOrNull(reasonCode)?.trim() || null;
      const shouldTreatAsIssue = statusText
         ? !this.isSuccessfulTransactionStatus(statusText)
         : Boolean(reasonTextValue || reasonCodeValue);

      if (!shouldTreatAsIssue) return null;
      /** Single user-facing copy for all pipeline / DB quality issues (map "!", table selection issue). */
      return Localization.getText("issue:generic");
   }

   public static getSelectionLockState(
      property: Pick<
         JPBReferenceProperty,
         "area" | "overlapArea" | "prisDriftsbygning" | "prisStuehus" | "transactionStatus" | "reasonCode" | "reasonText"
      >,
      assumptions?: Partial<JPBAssumptions> | JPBAssumptions
   ): { isLocked: boolean; source: "building" | "issue" | null; issueText: string | null } {
      const issueText = this.getTransactionIssueText(
         property.transactionStatus,
         property.reasonText,
         property.reasonCode
      );
      if (issueText) {
         return { isLocked: true, source: "issue", issueText };
      }

      const resolvedAssumptions = getResolvedJPBAssumptions(assumptions);
      const thresholdM2 = resolvedAssumptions.buildingAreaOkM2 ?? 0;
      const useAgriculturalArea =
         resolvedAssumptions.buildingAreaOkUseAgriculturalArea ?? DEFAULT_ASSUMPTIONS.buildingAreaOkUseAgriculturalArea ?? false;
      const hasBuildingValue =
         (typeof property.prisDriftsbygning === "number" && Number.isFinite(property.prisDriftsbygning) && property.prisDriftsbygning > 0) ||
         (typeof property.prisStuehus === "number" && Number.isFinite(property.prisStuehus) && property.prisStuehus > 0);
      const markOverlapPct = property.overlapArea?.markOverlap ?? 0;
      const buildingAreaM2 = useAgriculturalArea ? property.area * (markOverlapPct / 100) : property.area;
      const buildingLocked = hasBuildingValue && thresholdM2 > 0 && buildingAreaM2 < thresholdM2;

      return {
         isLocked: buildingLocked,
         source: buildingLocked ? "building" : null,
         issueText: null
      };
   }

   /** Context for deriving why a transaction is not checked (table + map hover). */
   public static getJPBExclusionExplanation(
      property: JPBReferenceProperty,
      ctx: {
         checkedItems: Set<string>;
         assumptions: JPBAssumptions;
         areaGrouping: AreaGrouping;
         priceMode: PriceMode;
      }
   ): string | null {
      if (ctx.checkedItems.has(property.documentID)) {
         return null;
      }

      const lockState = this.getSelectionLockState(property, ctx.assumptions);
      if (lockState.source === "issue" && lockState.issueText) {
         return lockState.issueText;
      }

      const assumptionFail = this.getAssumptionFilterFailureExplanation(property, {
         assumptions: ctx.assumptions,
         areaGrouping: ctx.areaGrouping,
         priceMode: ctx.priceMode
      });
      if (assumptionFail) {
         return assumptionFail;
      }

      return Localization.getText("jpb:reasonManuallyUnchecked");
   }


   /** Order tuned for UX: mark overlap and blanket building exclusion before the minimum-land rule (avoids misleading copy when total area is large but agricultural share is small). */
   private static getAssumptionFilterFailureExplanation(
      prop: JPBReferenceProperty,
      ctx: { assumptions: JPBAssumptions; areaGrouping: AreaGrouping; priceMode: PriceMode }
   ): string | null {
      const assumptions = ctx.assumptions;
      const markOverlap = prop.overlapArea?.markOverlap ?? 0;
      const hasBuildingValue =
         (typeof prop.prisDriftsbygning === "number" && Number.isFinite(prop.prisDriftsbygning) && prop.prisDriftsbygning > 0) ||
         (typeof prop.prisStuehus === "number" && Number.isFinite(prop.prisStuehus) && prop.prisStuehus > 0);
      const thresholdM2 = assumptions.buildingAreaOkM2 ?? 0;
      const buildingAreaM2 = assumptions.buildingAreaOkUseAgriculturalArea
         ? prop.area * (markOverlap / 100)
         : prop.area;
      const buildingAreaOk = !hasBuildingValue || thresholdM2 <= 0 || buildingAreaM2 >= thresholdM2;

      const pricePerSquareMeterToCheck =
         ctx.priceMode === PriceMode.Landbrug
            ? calculateEffectiveMarkSquareMeterPrice(prop)
            : prop.pricePerSquareMeter;

      const hasValidPrice =
         typeof pricePerSquareMeterToCheck === "number" && Number.isFinite(pricePerSquareMeterToCheck);

      if (ctx.areaGrouping === AreaGrouping.Under30Hektar && prop.area >= 300000) {
         return Localization.getText("jpb:ruleAreaGroupingUnder30");
      }
      if (ctx.areaGrouping === AreaGrouping.Over30Hektar && prop.area < 300000) {
         return Localization.getText("jpb:ruleAreaGroupingOver30");
      }
      if (!assumptions.includeTransactionTypes.includes(prop.transactionType)) {
         return Localization.getText("jpb:ruleTransactionTypeExcluded");
      }
      if (!hasValidPrice) {
         return Localization.getText("jpb:ruleInvalidPrice");
      }
      if (pricePerSquareMeterToCheck < assumptions.pricePerMeterMin) {
         return Localization.getFormattedText("jpb:rulePriceBelowMin", {
            price: formatPricePerSquareMeter(pricePerSquareMeterToCheck, AreaUnit.SquareMeter, 2, true),
            min: formatPricePerSquareMeter(assumptions.pricePerMeterMin, AreaUnit.SquareMeter, 2, true)
         });
      }
      if (pricePerSquareMeterToCheck > assumptions.pricePerMeterMax) {
         return Localization.getFormattedText("jpb:rulePriceAboveMax", {
            price: formatPricePerSquareMeter(pricePerSquareMeterToCheck, AreaUnit.SquareMeter, 2, true),
            max: formatPricePerSquareMeter(assumptions.pricePerMeterMax, AreaUnit.SquareMeter, 2, true)
         });
      }
      if (prop.area < assumptions.areaMin) {
         return Localization.getFormattedText("jpb:ruleAreaBelowMin", {
            area: formatArea(prop.area, AreaUnit.SquareMeter),
            min: formatArea(assumptions.areaMin, AreaUnit.SquareMeter)
         });
      }
      if (prop.area > assumptions.areaMax) {
         return Localization.getFormattedText("jpb:ruleAreaAboveMax", {
            area: formatArea(prop.area, AreaUnit.SquareMeter),
            max: formatArea(assumptions.areaMax, AreaUnit.SquareMeter)
         });
      }
      if (markOverlap <= assumptions.effectiveMarkOverlapThreshold) {
         return Localization.getFormattedText("jpb:ruleMarkOverlapLow", {
            overlap: formatPercentage(markOverlap),
            threshold: formatPercentage(assumptions.effectiveMarkOverlapThreshold)
         });
      }
      if (assumptions.excludeBuildings && hasBuildingValue) {
         return Localization.getText("jpb:ruleBuildingsExcluded");
      }
      if (!buildingAreaOk) {
         return Localization.getText("jpb:ruleBuildingLandAreaTooSmall");
      }

      return null;
   }

   public static normalizePriceIndexSource(value: unknown): JPBPriceIndexSource {
      if (value === JPBPriceIndexSource.Molbak) return JPBPriceIndexSource.Molbak;
      if (value === JPBPriceIndexSource.None) return JPBPriceIndexSource.None;
      return JPBPriceIndexSource.AgroCura;
   }

   public static getPriceIndexSourceLabel(source: JPBPriceIndexSource): string {
      if (source === JPBPriceIndexSource.Molbak) return Localization.getText("Mølbak index");
      if (source === JPBPriceIndexSource.None) return Localization.getText("No index");
      return Localization.getText("AgroCura index");
   }

   private static parseQuarterKey(dateValue: Date): `${number}K${number}` | null {
      if (!dateValue || Number.isNaN(dateValue.getTime())) return null;
      return `${dateValue.getFullYear()}K${Math.floor(dateValue.getMonth() / 3) + 1}`;
   }

   private static quarterKeyToSortValue(periodKey: string): number {
      const match = periodKey.match(/^(\d{4})K([1-4])$/);
      if (!match) return Number.MAX_SAFE_INTEGER;
      return Number(match[1]) * 10 + Number(match[2]);
   }

   private static getPreviousQuarterKey(periodKey: string): string | null {
      const match = periodKey.match(/^(\d{4})K([1-4])$/);
      if (!match) return null;
      const year = Number(match[1]);
      const quarter = Number(match[2]);
      if (quarter > 1) return `${year}K${quarter - 1}`;
      return `${year - 1}K4`;
   }

   private static getCurrentQuarterKey(nowDate: Date = new Date()): `${number}K${number}` | null {
      return this.parseQuarterKey(nowDate);
   }

   private static clampToLatestCompletedQuarter(periodKey: string, nowDate: Date = new Date()): string {
      const currentQuarterKey = this.getCurrentQuarterKey(nowDate);
      if (!currentQuarterKey) return periodKey;
      const periodSort = this.quarterKeyToSortValue(periodKey);
      const currentSort = this.quarterKeyToSortValue(currentQuarterKey);
      if (periodSort < currentSort) return periodKey;
      return this.getPreviousQuarterKey(currentQuarterKey) || periodKey;
   }

   private static isVisibleCompletedQuarter(periodKey: string, nowDate: Date = new Date()): boolean {
      const currentQuarterKey = this.getCurrentQuarterKey(nowDate);
      if (!currentQuarterKey) return true;
      return this.quarterKeyToSortValue(periodKey) < this.quarterKeyToSortValue(currentQuarterKey);
   }

   private static getCanonicalLandsdelList(): string[] {
      const municipalities = (JPBRegions?.municipalities || []) as Array<{ landsdel?: string }>;
      const set = new Set<string>();
      municipalities.forEach((municipality) => {
         if (municipality?.landsdel) set.add(municipality.landsdel);
      });
      return [...set].sort((a, b) => a.localeCompare(b, "da"));
   }

   private static getMunicipalityFromKommuneCode(kommune: string): { landsdel?: string; landsdelNr?: number } | null {
      const numericCode = Number.parseInt(String(kommune || "").trim(), 10);
      if (!Number.isFinite(numericCode)) return null;
      const municipalities = (JPBRegions?.municipalities || []) as Array<{ kommunenr?: number; landsdel?: string; landsdelNr?: number }>;
      return municipalities.find((municipality) => municipality.kommunenr === numericCode) || null;
   }

   private static resolveLandsdelForKommune(kommune: string): string {
      const municipality = this.getMunicipalityFromKommuneCode(kommune);
      if (municipality?.landsdel) return municipality.landsdel;
      if (municipality?.landsdelNr !== undefined) {
         const mappedRegion = LANDSDEL_NR_TO_REGION_MAP[municipality.landsdelNr];
         if (mappedRegion === "Landsdel Fyn") return "Fyn";
         if (mappedRegion === "Landsdel Sydjylland") return "Sydjylland";
         if (mappedRegion === "Landsdel Østjylland") return "Østjylland";
         if (mappedRegion === "Landsdel Vestjylland") return "Vestjylland";
         if (mappedRegion === "Landsdel Nordjylland") return "Nordjylland";
      }
      return "Vest- og Sydsjælland";
   }

   private static resolveAgroRegionName(landsdel: string): string {
      return LANDSDEL_TO_REGION_MAP[landsdel] || DEFAULT_AGRO_REGION_NAME;
   }

   private static getAgroRegionByLandsdel(landsdel: string): { name: string; values: { [key: string]: number } } | null {
      const regionName = this.resolveAgroRegionName(landsdel);
      const regions = ((JPBIndexData as any)?.regions || []) as Array<{ name: string; values: { [key: string]: number } }>;
      return regions.find((region) => region.name === regionName) ||
         regions.find((region) => region.name === DEFAULT_AGRO_REGION_NAME) ||
         null;
   }

   private static normalizeFactorSeries(values: { [key: string]: number } | undefined): { [periodKey: string]: number | null } {
      const out: { [periodKey: string]: number | null } = {};
      if (!values) return out;
      const sortedEntries = Object.entries(values).sort((a, b) => this.quarterKeyToSortValue(a[0]) - this.quarterKeyToSortValue(b[0]));
      const base = sortedEntries.find(([, value]) => typeof value === "number" && Number.isFinite(value) && value > 0)?.[1];
      if (!base || !Number.isFinite(base) || base <= 0) {
         sortedEntries.forEach(([periodKey]) => {
            out[periodKey] = null;
         });
         return out;
      }
      sortedEntries.forEach(([periodKey, value]) => {
         out[periodKey] = typeof value === "number" && Number.isFinite(value) && value > 0 ? value / base : null;
      });
      return out;
   }

   public static getAgroNormalizedFactorMapForLandsdel(landsdel: string): { [periodKey: string]: number | null } {
      const region = this.getAgroRegionByLandsdel(landsdel);
      return this.normalizeFactorSeries(region?.values);
   }

   private static getLastFiniteFactor(factorsByPeriod: { [periodKey: string]: number | null }): number | null {
      const sortedKeys = Object.keys(factorsByPeriod).sort((a, b) => this.quarterKeyToSortValue(a) - this.quarterKeyToSortValue(b));
      for (let i = sortedKeys.length - 1; i >= 0; i--) {
         const factor = factorsByPeriod[sortedKeys[i]];
         if (typeof factor === "number" && Number.isFinite(factor) && factor > 0) return factor;
      }
      return null;
   }

   private static resolveFactorWithFallback(
      periodKey: string,
      factorsByPeriod: { [periodKey: string]: number | null }
   ): number | null {
      const direct = factorsByPeriod[periodKey];
      if (typeof direct === "number" && Number.isFinite(direct) && direct > 0) return direct;
      return this.getLastFiniteFactor(factorsByPeriod);
   }

   private static getMolbakDocumentForLandsdel(
      landsdel: string,
      preparedState: JPBIndexedLandsdelState | null
   ): JPBIndexedLandsdelDocument | null {
      if (!preparedState) return null;
      // Use newest uploaded variant for this landsdel (regardless of smoothing/settings),
      // since data has already been fetched and sorted by generatedAt desc.
      return preparedState.byLandsdel[landsdel]?.[0] || null;
   }

   private static getMolbakAverageTxCount(document: JPBIndexedLandsdelDocument): number | null {
      if (!document.periods.length) return null;
      const txSum = document.periods.reduce((sum, period) => sum + (period.txCount || 0), 0);
      return txSum / document.periods.length;
   }

   private static normalizeFactorMapToStartAtOne(
      factorMap: { [periodKey: string]: number | null },
      preferredBasePeriod: string | null
   ): { [periodKey: string]: number | null } {
      const sortedKeys = Object.keys(factorMap).sort((a, b) => this.quarterKeyToSortValue(a) - this.quarterKeyToSortValue(b));
      if (!sortedKeys.length) return factorMap;
      const preferredBaseValue = preferredBasePeriod ? factorMap[preferredBasePeriod] : null;
      const anchorValue = (typeof preferredBaseValue === "number" && Number.isFinite(preferredBaseValue) && preferredBaseValue > 0)
         ? preferredBaseValue
         : sortedKeys
            .map((key) => factorMap[key])
            .find((value) => typeof value === "number" && Number.isFinite(value) && value > 0);
      if (!(typeof anchorValue === "number" && Number.isFinite(anchorValue) && anchorValue > 0)) {
         return factorMap;
      }
      const normalized: { [periodKey: string]: number | null } = {};
      sortedKeys.forEach((periodKey) => {
         const value = factorMap[periodKey];
         normalized[periodKey] = (typeof value === "number" && Number.isFinite(value) && value > 0)
            ? value / anchorValue
            : null;
      });
      return normalized;
   }

   private static buildMolbakFittedFactorMap(document: JPBIndexedLandsdelDocument): { [periodKey: string]: number | null } {
      const out: { [periodKey: string]: number | null } = {};
      const byPrecomputedFit = document.periods.every((period) => typeof period.fitFactor === "number" && Number.isFinite(period.fitFactor) && period.fitFactor > 0);
      if (byPrecomputedFit) {
         document.periods.forEach((period) => {
            out[period.periodKey] = period.fitFactor ?? null;
         });
         return this.normalizeFactorMapToStartAtOne(out, document.basePeriod);
      }

      if (!document.fitExp) {
         document.periods.forEach((period) => {
            out[period.periodKey] = null;
         });
         return out;
      }

      const baseIndex = document.basePeriod
         ? document.periods.findIndex((period) => period.periodKey === document.basePeriod)
         : 0;
      const normalizedBaseIndex = baseIndex >= 0 ? baseIndex : 0;
      const baseFitValue = document.fitExp.a * Math.exp(document.fitExp.b * normalizedBaseIndex);
      if (!(baseFitValue > 0) || !Number.isFinite(baseFitValue)) {
         document.periods.forEach((period) => {
            out[period.periodKey] = null;
         });
         return out;
      }

      document.periods.forEach((period, idx) => {
         const fitValue = document.fitExp!.a * Math.exp(document.fitExp!.b * idx);
         out[period.periodKey] = Number.isFinite(fitValue) && fitValue > 0 ? fitValue / baseFitValue : null;
      });
      return this.normalizeFactorMapToStartAtOne(out, document.basePeriod);
   }

   private static buildMolbakAppliedFactorRow(document: JPBIndexedLandsdelDocument): JPBPriceIndexLandsdelFactorRow {
      const avgTxCount = this.getMolbakAverageTxCount(document);
      const usesFittedModel = avgTxCount !== null && avgTxCount < MOLBAK_LOW_SAMPLE_AVG_TX_THRESHOLD;
      const fittedFactors = this.buildMolbakFittedFactorMap(document);
      const factorsByPeriod: { [periodKey: string]: number | null } = {};

      document.periods.forEach((period) => {
         const rawFactor = typeof period.factor === "number" && Number.isFinite(period.factor) && period.factor > 0
            ? period.factor
            : null;
         if (usesFittedModel) {
            factorsByPeriod[period.periodKey] = fittedFactors[period.periodKey] ?? rawFactor;
         } else {
            factorsByPeriod[period.periodKey] = rawFactor ?? fittedFactors[period.periodKey] ?? null;
         }
      });

      return {
         landsdel: document.landsdel,
         avgTxCount,
         usesFittedModel,
         factorsByPeriod
      };
   }

   public static buildIndexedLandsdelLookupKey(doc: Pick<
      JPBIndexedLandsdelDocument,
      | "computationMethod"
      | "landsdel"
      | "interval"
      | "statistic"
      | "smoothingMode"
      | "dynamicBucketGeneralization"
      | "hovedstadenGroupingMode"
      | "buildingAreaThresholdHa"
      | "pricePerM2Min"
      | "pricePerM2Max"
      | "subtractBuildingPrices"
   >): string {
      const base = `${doc.landsdel}__${doc.interval}__${doc.statistic}`.replace(/\s+/g, "_");
      const suffixes: string[] = [];
      if (doc.computationMethod === "quarterlyShrinkage") suffixes.push("cm-qshr");
      if (doc.smoothingMode !== "none") suffixes.push(`sm-${doc.smoothingMode}`);
      if (doc.dynamicBucketGeneralization) suffixes.push("dbg-1");
      if (doc.landsdel === HOVEDSTADEN_LANDSDEL && doc.hovedstadenGroupingMode && doc.hovedstadenGroupingMode !== "none") {
         suffixes.push(
            doc.hovedstadenGroupingMode === "withNordsjaelland" ? "hg-ns" : "hg-ns-os"
         );
      }
      if (doc.buildingAreaThresholdHa > 0) suffixes.push(`ba-${doc.buildingAreaThresholdHa.toString().replace(".", "_")}ha`);
      if (typeof doc.pricePerM2Min === "number" && Number.isFinite(doc.pricePerM2Min)) {
         suffixes.push(`ppm2min-${doc.pricePerM2Min.toString().replace(".", "_")}`);
      }
      if (typeof doc.pricePerM2Max === "number" && Number.isFinite(doc.pricePerM2Max)) {
         suffixes.push(`ppm2max-${doc.pricePerM2Max.toString().replace(".", "_")}`);
      }
      if (doc.subtractBuildingPrices) suffixes.push("sb-1");
      if (!suffixes.length) return base;
      return `${base}__${suffixes.join("__")}`;
   }

   public static getIndexedLandsdelState(): JPBIndexedLandsdelState | null {
      return JPBManager.indexedLandsdelStateCache;
   }

   public static async prepareIndexedLandsdelState(forceRefresh: boolean = false): Promise<JPBIndexedLandsdelState | null> {
      const indexName = this.getIndexedLandsdelIndexName();
      const now = Date.now();
      if (
         !forceRefresh &&
         JPBManager.indexedLandsdelStateCache &&
         JPBManager.indexedLandsdelStateLastFetched &&
         JPBManager.indexedLandsdelStateCache.indexName === indexName &&
         (now - JPBManager.indexedLandsdelStateLastFetched) < JPBManager.indexedLandsdelStateCacheTimeoutMs
      ) {
         return JPBManager.indexedLandsdelStateCache;
      }

      if (!forceRefresh && JPBManager.indexedLandsdelStatePromise) {
         return JPBManager.indexedLandsdelStatePromise;
      }

      const loadPromise = (async () => {
         try {
            const es = new ESInterfaceJWTAuth(indexName);
            const response = await es.doQuery({
               size: 5000,
               sort: [{ generatedAt: "desc" }, { landsdel: "asc" }],
               query: { match_all: {} }
            });
            const hits = response?.hits?.hits || [];
            const documents = hits
               .map((hit: any) => this.normalizeIndexedLandsdelDocument(hit?._source))
               .filter((doc: JPBIndexedLandsdelDocument | null): doc is JPBIndexedLandsdelDocument => Boolean(doc));

            const byLandsdel: { [landsdel: string]: JPBIndexedLandsdelDocument[] } = {};
            const byLookupKey: { [lookupKey: string]: JPBIndexedLandsdelDocument } = {};
            documents.forEach((doc) => {
               if (!byLandsdel[doc.landsdel]) {
                  byLandsdel[doc.landsdel] = [];
               }
               byLandsdel[doc.landsdel].push(doc);
               const lookupKey = this.buildIndexedLandsdelLookupKey(doc);
               if (!byLookupKey[lookupKey]) {
                  byLookupKey[lookupKey] = doc;
               }
            });

            const landsdele = Object.keys(byLandsdel).sort((a, b) => a.localeCompare(b, "da"));
            const preparedState: JPBIndexedLandsdelState = {
               indexName,
               fetchedAt: Date.now(),
               documents,
               landsdele,
               byLandsdel,
               byLookupKey
            };
            JPBManager.indexedLandsdelStateCache = preparedState;
            JPBManager.indexedLandsdelStateLastFetched = Date.now();
            return preparedState;
         } catch (error) {
            console.warn("Failed to prepare indexed landsdel state:", error);
            JPBManager.indexedLandsdelStateCache = null;
            JPBManager.indexedLandsdelStateLastFetched = Date.now();
            return null;
         }
      })();

      JPBManager.indexedLandsdelStatePromise = loadPromise;
      try {
         return await loadPromise;
      } finally {
         if (JPBManager.indexedLandsdelStatePromise === loadPromise) {
            JPBManager.indexedLandsdelStatePromise = null;
         }
      }
   }

   public static async getPriceIndexFactorsTable(
      sourceInput: JPBPriceIndexSource,
      forceRefresh: boolean = false
   ): Promise<JPBPriceIndexFactorsTable> {
      const source = this.normalizePriceIndexSource(sourceInput);

      if (source === JPBPriceIndexSource.None) {
         return {
            source,
            periodKeys: [],
            rows: this.getCanonicalLandsdelList().map((landsdel) => ({
               landsdel,
               avgTxCount: null,
               usesFittedModel: false,
               factorsByPeriod: {}
            }))
         };
      }

      if (source === JPBPriceIndexSource.AgroCura) {
         const rows = this.getCanonicalLandsdelList().map((landsdel) => {
            const region = this.getAgroRegionByLandsdel(landsdel);
            const factorsByPeriod = this.normalizeFactorSeries(region?.values);
            return {
               landsdel,
               avgTxCount: null,
               usesFittedModel: false,
               factorsByPeriod
            };
         });
         const periodKeys = [...new Set(rows.flatMap((row) => Object.keys(row.factorsByPeriod)))]
            .filter((periodKey) => this.isVisibleCompletedQuarter(periodKey))
            .sort((a, b) => this.quarterKeyToSortValue(a) - this.quarterKeyToSortValue(b));
         return { source, periodKeys, rows };
      }

      const preparedState = await this.prepareIndexedLandsdelState(forceRefresh);
      const landsdele = preparedState?.landsdele || [];
      const rows = landsdele.map((landsdel) => {
         const doc = this.getMolbakDocumentForLandsdel(landsdel, preparedState);
         if (!doc) {
            return {
               landsdel,
               avgTxCount: null,
               usesFittedModel: false,
               factorsByPeriod: {}
            };
         }
         return this.buildMolbakAppliedFactorRow(doc);
      });
      const periodKeys = [...new Set(rows.flatMap((row) => Object.keys(row.factorsByPeriod)))]
         .filter((periodKey) => this.isVisibleCompletedQuarter(periodKey))
         .sort((a, b) => this.quarterKeyToSortValue(a) - this.quarterKeyToSortValue(b));
      return { source, periodKeys, rows };
   }

   private static normalizeIndexedLandsdelDocument(source: any): JPBIndexedLandsdelDocument | null {
      if (!source || typeof source !== "object") return null;
      if (typeof source.landsdel !== "string" || source.landsdel.length === 0) return null;
      const computationMethod = source.computationMethod === "quarterlyShrinkage"
         ? "quarterlyShrinkage"
         : "standard";
      const interval =
         source.interval === "quarter" || source.interval === "halfYear" || source.interval === "fullYear"
            ? source.interval
            : "quarter";
      const statistic = source.statistic === "mean" || source.statistic === "median" ? source.statistic : "median";
      const smoothingMode = source.smoothingMode === "windowPlusMinusOne" || source.smoothingMode === "none"
         ? source.smoothingMode
         : "none";

      const periodsRaw = Array.isArray(source.periods) ? source.periods : [];
      const periods: JPBIndexedLandsdelPeriod[] = periodsRaw
         .map((period: any) => {
            if (!period || typeof period !== "object") return null;
            if (typeof period.periodKey !== "string" || period.periodKey.length === 0) return null;
            const dynamicBucketRule = this.toStringOrNull(period.dynamicBucketRule) ?? undefined;
            const normalizedHalfWindowQuarters = Math.max(
               0,
               Math.floor(this.toFiniteNumber(period.dynamicHalfWindowQuarters) || 0)
            );
            const dynamicHalfWindowQuarters =
               normalizedHalfWindowQuarters > 0
                  ? normalizedHalfWindowQuarters
                  : this.legacyDynamicRuleToHalfWindow(dynamicBucketRule);
            const hasDynamicBucket = Boolean(dynamicBucketRule) || Boolean(dynamicHalfWindowQuarters);
            const dynamicRelativeCi95 =
               typeof period.dynamicRelativeCi95 === "number" && Number.isFinite(period.dynamicRelativeCi95)
                  ? period.dynamicRelativeCi95
                  : null;
            return {
               periodKey: period.periodKey,
               periodStart: this.toEpochMilliseconds(period.periodStart),
               periodEnd: this.toEpochMilliseconds(period.periodEnd),
               value: this.toFiniteNumber(period.value),
               factor: this.toFiniteNumber(period.factor),
               txCount: Math.max(0, Math.floor(this.toFiniteNumber(period.txCount) || 0)),
               dynamicBucketRule: hasDynamicBucket ? (dynamicBucketRule as JPBDynamicBucketRule | undefined) : undefined,
               dynamicHalfWindowQuarters: hasDynamicBucket ? dynamicHalfWindowQuarters : undefined,
               dynamicRelativeCi95: hasDynamicBucket ? dynamicRelativeCi95 : undefined,
               shrinkageWeight: this.toFiniteNumber(period.shrinkageWeight),
               shrinkageRawValue: this.toFiniteNumber(period.shrinkageRawValue),
               shrinkagePriorValue: this.toFiniteNumber(period.shrinkagePriorValue),
               fitFactor: this.toFiniteNumber(period.fitFactor),
               fitValue: this.toFiniteNumber(period.fitValue)
            };
         })
         .filter((period: JPBIndexedLandsdelPeriod | null): period is JPBIndexedLandsdelPeriod => Boolean(period))
         .sort((a, b) => {
            if (a.periodStart !== b.periodStart) return a.periodStart - b.periodStart;
            return this.quarterKeyToSortValue(a.periodKey) - this.quarterKeyToSortValue(b.periodKey);
         });

      const fitExpRaw = source.fitExp;
      const fitA = fitExpRaw && typeof fitExpRaw === "object" ? this.toFiniteNumber(fitExpRaw.a) : null;
      const fitB = fitExpRaw && typeof fitExpRaw === "object" ? this.toFiniteNumber(fitExpRaw.b) : null;
      const fitRSquared = fitExpRaw && typeof fitExpRaw === "object" ? this.toFiniteNumber(fitExpRaw.rSquared) : null;
      const fitExp = fitA !== null && fitB !== null && fitRSquared !== null
         ? {
            a: fitA,
            b: fitB,
            rSquared: fitRSquared,
            rmse: this.toFiniteNumber(fitExpRaw.rmse)
         }
         : undefined;

      return {
         landsdel: source.landsdel,
         computationMethod,
         interval,
         statistic,
         smoothingMode,
         dynamicBucketGeneralization: Boolean(source.dynamicBucketGeneralization),
         hovedstadenGroupingMode: this.normalizeHovedstadenGroupingMode(source.hovedstadenGroupingMode),
         buildingAreaThresholdHa: this.toFiniteNumber(source.buildingAreaThresholdHa) ?? 0,
         pricePerM2Min: this.toFiniteNumber(source.pricePerM2Min),
         pricePerM2Max: this.toFiniteNumber(source.pricePerM2Max),
         subtractBuildingPrices: Boolean(source.subtractBuildingPrices),
         yearsBack: Math.max(1, Math.floor(this.toFiniteNumber(source.yearsBack) ?? 1)),
         generatedAt: this.toEpochMilliseconds(source.generatedAt),
         basePeriod: this.toStringOrNull(source.basePeriod),
         periods,
         ...(fitExp ? { fitExp } : {})
      };
   }

   public static async fetchDatabaseMetadata(forceRefresh: boolean = false): Promise<JPBDatabaseMetadata | null> {
      // Cache for 5 minutes
      const cacheTimeout = 5 * 60 * 1000; // 5 minutes in milliseconds
      const now = Date.now();

      if (!forceRefresh && JPBManager.databaseMetadata && JPBManager.metadataLastFetched && (now - JPBManager.metadataLastFetched) < cacheTimeout) {
         return JPBManager.databaseMetadata;
      }

      try {
         const indexName = SettingsManager.getSystemSetting("jpb.indexName", "jpb_landbrugshandler_testv5");
         const metaIndexName = `${indexName}__meta`;
         const metaESInterface = new ESInterfaceJWTAuth(metaIndexName);

         // Use query instead of getDocument to handle stored fields and encoding issues
         const queryBody = {
            query: {
               ids: {
                  values: ["skødeArealer_latest"]
               }
            },
            stored_fields: ["*"],
            _source: true
         };

         const response = await metaESInterface.doQuery(queryBody);

         const hits = response.hits?.hits || [];
         if (hits.length === 0) {
            return null;
         }

         const hit = hits[0];
         const source = hit._source || {};
         const fields = hit.fields || {};

         // Prefer fields if available, otherwise use _source
         const dataset = fields.dataset?.[0] || fields["dataset.keyword"]?.[0] || source.dataset || "";
         const targetIndex = fields.targetIndex?.[0] || fields["targetIndex.keyword"]?.[0] || source.targetIndex || "";
         const uploadBatchId = fields.uploadBatchId?.[0] || source.uploadBatchId || "";
         const uploadedDocs = fields.uploadedDocs?.[0] || source.uploadedDocs || 0;
         const uploadedAt = JPBManager.toEpochMilliseconds(fields.uploadedAt?.[0] || source.uploadedAt || 0);
         const esDocCountAfter = fields.esDocCountAfter?.[0] || source.esDocCountAfter || 0;

         JPBManager.databaseMetadata = {
            dataset,
            targetIndex,
            uploadBatchId,
            uploadedDocs,
            uploadedAt,
            esDocCountAfter
         };

         JPBManager.metadataLastFetched = now;
         return JPBManager.databaseMetadata;
      } catch {
         return null;
      }
   }

   public static getDatabaseMetadata(): JPBDatabaseMetadata | null {
      return JPBManager.databaseMetadata;
   }

   public static async fetchDateBounds(forceRefresh: boolean = false): Promise<JPBDateBounds | null> {
      const indexName = SettingsManager.getSystemSetting("jpb.indexName", "jpb_landbrugshandler_testv5");
      const now = Date.now();
      const cached = JPBManager.dateBoundsCache;
      if (
         !forceRefresh &&
         cached &&
         cached.indexName === indexName &&
         (now - cached.fetchedAt) < JPBManager.dateBoundsCacheTimeoutMs
      ) {
         return {
            minDate: new Date(cached.minDateMs),
            maxDate: new Date(cached.maxDateMs)
         };
      }

      const es = JPBManager.getESInterfaceJWTAuth();
      try {
         const [firstResponse, lastResponse] = await Promise.all([
            es.doQuery({
               size: 1,
               sort: [{ dato: "asc" }, { _seq_no: "asc" }],
               _source: ["dato"]
            }),
            es.doQuery({
               size: 1,
               sort: [{ dato: "desc" }, { _seq_no: "desc" }],
               _source: ["dato"]
            })
         ]);

         const firstRaw = firstResponse?.hits?.hits?.[0]?._source?.dato;
         const lastRaw = lastResponse?.hits?.hits?.[0]?._source?.dato;
         const minDateMs = JPBManager.toEpochMilliseconds(firstRaw);
         const maxDateMs = JPBManager.toEpochMilliseconds(lastRaw);

         if (!(minDateMs > 0) || !(maxDateMs > 0)) {
            return null;
         }

         const min = Math.min(minDateMs, maxDateMs);
         const max = Math.max(minDateMs, maxDateMs);
         JPBManager.dateBoundsCache = {
            indexName,
            minDateMs: min,
            maxDateMs: max,
            fetchedAt: now
         };

         return {
            minDate: new Date(min),
            maxDate: new Date(max)
         };
      } catch {
         return null;
      }
   }

   private static async fetchAllDataWithPagination(
      indexName: string,
      baseQuery: any,
      maxRows: number = 1000,
      onProgress?: (progress: { totalFetched: number; total: number; currentPage: number; sizeMB: number }) => void,
      abortSignal?: AbortSignal
   ): Promise<any> {
      const allHits: any[] = [];
      let currentPage = 0;
      let total: number | null = null;
      let searchAfter: any[] | null = null;
      let hasMore = true;
      const maxPages = 10000; // Safety limit

      // Ensure query has sort for search_after
      // Note: Can't use _id for sorting (fielddata disabled), so we use dato only
      // If dato has duplicates, we'll use _seq_no as tiebreaker (available in ES responses)
      const queryWithSort = baseQuery.sort
         ? baseQuery
         : { ...baseQuery, sort: [{ dato: 'desc' }, { _seq_no: 'desc' }] };

      // Get noSQLDataUrl and JWT token
      const noSQLDataUrl = SettingsManager.getSystemSetting("getNoSQLDataUrl");

      // Get JWT token (similar to ESInterfaceJWTAuth)
      const getJwtToken = (): string | undefined => {
         try {
            const authData = SettingsManager.getSystemSetting("authData");
            if (!authData || !authData.ClientId) {
               return undefined;
            }
            const lastAuthUserKey = `CognitoIdentityServiceProvider.${authData.ClientId}.LastAuthUser`;
            const username = localStorage.getItem(lastAuthUserKey);
            if (!username) {
               return undefined;
            }
            const idTokenKey = `CognitoIdentityServiceProvider.${authData.ClientId}.${username}.idToken`;
            return localStorage.getItem(idTokenKey) || undefined;
         } catch {
            return undefined;
         }
      };

      while (hasMore && currentPage < maxPages) {
         if (abortSignal?.aborted) {
            throw new Error("Request aborted");
         }
         currentPage++;

         try {
            // Build Elasticsearch query
            const esQuery: any = {
               ...queryWithSort,
               size: maxRows
            };

            if (searchAfter) {
               esQuery.search_after = searchAfter;
            }

            // Call Lambda with pagination parameters (format expected by Lambda)
            const requestBody = {
               index: `/${indexName}/_search`,
               query: esQuery,
               maxRows: maxRows,
               ...(searchAfter && { searchAfter: searchAfter })
            };

            const jwtToken = getJwtToken();

            const response = await fetch(noSQLDataUrl, {
               method: "POST",
               signal: abortSignal,
               headers: {
                  "content-type": "application/json",
                  ...(jwtToken && { "Authorization": `Bearer ${jwtToken}` })
               },
               body: JSON.stringify(requestBody)
            });

            if (response.status !== 200) {
               const errorText = await response.text();
               throw new Error(`HTTP ${response.status}: ${errorText}`);
            }

            const body = await response.json();

            // Handle pagination metadata from Lambda response
            const pagination = body.pagination;
            const hits = body.hits?.hits || [];
            allHits.push(...hits);

            // Store total from first response
            if (currentPage === 1) {
               const totalObj = body.hits?.total;
               total = typeof totalObj === 'object' ? totalObj.value : totalObj;
            } else if (pagination) {
               total = pagination.total;
            }

            // Update pagination state
            if (pagination) {
               hasMore = pagination.hasMore;
               searchAfter = pagination.nextSearchAfter;
            } else {
               // Fallback: check if we got fewer results than requested
               hasMore = hits.length === maxRows;
               if (hasMore && hits.length > 0) {
                  const lastHit = hits[hits.length - 1];
                  // Use sort array from hit, or construct from dato and _seq_no
                  if (lastHit.sort) {
                     searchAfter = lastHit.sort;
                  } else if (lastHit._source?.dato !== undefined && lastHit._seq_no !== undefined) {
                     searchAfter = [lastHit._source.dato, lastHit._seq_no];
                  } else {
                     // Last resort: just use dato if available
                     searchAfter = lastHit._source?.dato !== undefined ? [lastHit._source.dato] : null;
                  }
               }
            }

            // Progress callback
            if (onProgress) {
               // Calculate cumulative size in MB
               const cumulativeSizeBytes = new Blob([JSON.stringify({ hits: { hits: allHits } })]).size;
               const cumulativeSizeMB = cumulativeSizeBytes / (1024 * 1024);

               onProgress({
                  totalFetched: allHits.length,
                  total: total || allHits.length,
                  currentPage,
                  sizeMB: cumulativeSizeMB
               });
            }

            // Safety check: if we got no results, we're done
            if (hits.length === 0) {
               hasMore = false;
            }

         } catch (error: any) {
            throw new Error(`Failed to fetch page ${currentPage}: ${error.message || error}`);
         }
      }

      return {
         hits: allHits,
         total: total || allHits.length
      };
   }

   public static async fetchReferenceProperties(
      options: JPBQueryOptions = {},
      _abortSignal?: AbortSignal
   ): Promise<JPBReferenceProperty[]> {
      const {
         polygonCoordinates = [
            [
               [11.835630900078542, 55.60278752391599],
               [12.223742781132785, 55.53421053360438],
               [11.25270784583796, 55.35645062377134],
               [11.835630900078542, 55.60278752391599]
            ]
         ],
         yearGrouping = YearGrouping.FiveYears,
         calculationDateTo,
         priceIndexSource = JPBPriceIndexSource.AgroCura
      } = options;
      const normalizedPriceIndexSource = this.normalizePriceIndexSource(priceIndexSource);
      if (normalizedPriceIndexSource === JPBPriceIndexSource.Molbak) {
         await this.prepareIndexedLandsdelState(false);
      }

      const indexName = SettingsManager.getSystemSetting("jpb.indexName", "jpb_landbrugshandler_testv5");

      const metadata = await JPBManager.fetchDatabaseMetadata();

      const metadataDateMs = JPBManager.toEpochMilliseconds(metadata?.uploadedAt);
      const requestedToDateMs = calculationDateTo ? JPBManager.toEpochMilliseconds(calculationDateTo.getTime()) : 0;
      const fallbackToDateMs = metadataDateMs > 0 ? metadataDateMs : Date.now();
      const toDateMs = requestedToDateMs > 0 ? requestedToDateMs : fallbackToDateMs;
      const toDate = new Date(toDateMs);
      toDate.setHours(23, 59, 59, 999);

      const baseDate = toDate;
      const dateThreshold = new Date(baseDate);
      dateThreshold.setFullYear(dateThreshold.getFullYear() - yearGrouping);
      const dateThresholdTimestamp = Math.floor(dateThreshold.getTime());
      const toDateTimestamp = Math.floor(toDate.getTime());
      const dateThresholdTimestampSeconds = Math.floor(dateThresholdTimestamp / 1000);
      const toDateTimestampSeconds = Math.floor(toDateTimestamp / 1000);


      const isMultiPolygon = polygonCoordinates && polygonCoordinates.length > 1;

      let geoShapeType: string;
      let geoShapeCoordinates: any;

      if (isMultiPolygon) {
         geoShapeType = "MultiPolygon";
         geoShapeCoordinates = polygonCoordinates.map(ring => [ring]);
      } else {
         geoShapeType = "Polygon";
         geoShapeCoordinates = polygonCoordinates;
      }

      const baseQuery = {
         query: {
            bool: {
               filter: [
                  {
                     geo_shape: {
                        geopoint: {
                           shape: {
                              type: geoShapeType,
                              coordinates: geoShapeCoordinates
                           },
                           relation: "within"
                        }
                     }
                  },
                  {
                     bool: {
                        should: [
                           {
                              range: {
                                 dato: {
                                    gte: dateThresholdTimestamp,
                                    lte: toDateTimestamp
                                 }
                              }
                           },
                           {
                              range: {
                                 dato: {
                                    gte: dateThresholdTimestampSeconds,
                                    lte: toDateTimestampSeconds
                                 }
                              }
                           }
                        ],
                        minimum_should_match: 1
                     }
                  }
               ]
            }
         },
         version: true
      };

      const maxRowsPerPage = 50; // Small pages to handle geometries and stay under 6MB limit

      // Build query with sort for pagination (required for search_after)
      const baseQueryAny = baseQuery as any;
      const queryWithSort: any = baseQueryAny.sort
         ? baseQuery
         : { ...baseQuery, sort: [{ dato: 'desc' }, { _seq_no: 'desc' }] };

      const response = await this.fetchAllDataWithPagination(
         indexName,
         queryWithSort,
         maxRowsPerPage,
         undefined,
         _abortSignal
      );
      const allHits = response.hits || [];



      type ESType<T> = {
         _index: string;
         _id: string;
         _score: number;
         _source: T;
      }

      type JPBESType = {
         "id": string,
         "dokumentId"?: string,
         "matchedBfenummer"?: string,
         "geopoint": [number, number],
         "landbrugsnotering"?: string,
         "adresse"?: string,
         "bfenummer"?: string,
         "geoshape"?: any,
         "komkode"?: string,
         "m2total": number,
         "m2vej": number,
         "m2bygning"?: number,
         "m2skov"?: number,
         "m2landbrug"?: number,
         "jPris"?: number,
         "pris": number,
         "dato": number, // timestamp since epoch
         "type"?: string,
         "status"?: string,
         "error"?: number,
         "reasonCode"?: string,
         "reasonText"?: string,
         "prisDriftsbygning"?: number | null,
         "prisStuehus"?: number | null,
         "markOverlapPct"?: number | null,
         "markOverlapGeometry"?: any | null,
         "markOverlapLayerYear"?: number | null,
         "markOverlapCalculatedAt"?: number | null,
         "jordbundskortGeometry"?: any | null,
         "jordbundskortOverlap"?: { [soilType: string]: number } | null,
         "jordbundskortCalculatedAt"?: number | null,
      }

      const validProperties = allHits.map((hit: ESType<JPBESType>) => {
         const source = hit._source;
         const rowDocumentId = this.toStringOrNull(source.id) || hit._id || "";
         const deedDocumentId = this.toStringOrNull(source.dokumentId) || rowDocumentId;
         const matchedBfeNr = this.toStringOrNull(source.matchedBfenummer);
         const transactionStatus = this.toStringOrNull(source.status) || "";
         const landbrugsnotering = this.toStringOrNull(source.landbrugsnotering);
         const reasonCode = this.toStringOrNull(source.reasonCode);
         const reasonText = this.toStringOrNull(source.reasonText);

         const saleDate = new Date(source.dato);
         const kommune = source.komkode || "";

         const originalTotalPrice = source.pris || 0;
         const indexedTotalPrice = JPBManager.calculatePresentValue(
            originalTotalPrice,
            saleDate,
            kommune,
            toDate,
            normalizedPriceIndexSource
         );

         const area = source.m2total || 0;
         const originalPricePerSquareMeter = area > 0 ? originalTotalPrice / area : 0;

         const rawDriftsbygning = typeof source.prisDriftsbygning === "number" && Number.isFinite(source.prisDriftsbygning)
            ? source.prisDriftsbygning
            : null;
         const rawStuehus = typeof source.prisStuehus === "number" && Number.isFinite(source.prisStuehus)
            ? source.prisStuehus
            : null;

         const indexedDriftsbygning =
            rawDriftsbygning && rawDriftsbygning > 0
               ? JPBManager.calculatePresentValue(rawDriftsbygning, saleDate, kommune, toDate, normalizedPriceIndexSource)
               : null;
         const indexedStuehus =
            rawStuehus && rawStuehus > 0
               ? JPBManager.calculatePresentValue(rawStuehus, saleDate, kommune, toDate, normalizedPriceIndexSource)
               : null;

         const markOverlapPct = source.markOverlapPct ?? null;
         const markOverlapGeometry = source.markOverlapGeometry || null;
         const markOverlapLayerYear =
            typeof source.markOverlapLayerYear === "number" && Number.isFinite(source.markOverlapLayerYear)
               ? source.markOverlapLayerYear
               : null;
         const markOverlapCalculatedAt = this.toEpochMilliseconds(source.markOverlapCalculatedAt);

         const hasBuildingValue =
            (typeof indexedDriftsbygning === "number" && Number.isFinite(indexedDriftsbygning) && indexedDriftsbygning > 0) ||
            (typeof indexedStuehus === "number" && Number.isFinite(indexedStuehus) && indexedStuehus > 0);

         const buildingsIndexedSum =
            (indexedDriftsbygning ?? 0) + (indexedStuehus ?? 0);
         const buildingsOriginalSum =
            (rawDriftsbygning ?? 0) + (rawStuehus ?? 0);


         const indexedLandPrice = hasBuildingValue
            ? Math.max(0, indexedTotalPrice - buildingsIndexedSum)
            : indexedTotalPrice;
         const originalLandPrice = hasBuildingValue
            ? Math.max(0, originalTotalPrice - buildingsOriginalSum)
            : originalTotalPrice;

         const adjustedPricePerSquareMeter = area > 0 ? indexedLandPrice / area : 0;
         const indexDifferencePct =
            originalLandPrice > 0 && Number.isFinite(indexedLandPrice)
               ? ((indexedLandPrice - originalLandPrice) / originalLandPrice) * 100
               : null;

         // Parse jordbundskortGeometry if it's a stringified FeatureCollection
         let jordbundskortGeometry = source.jordbundskortGeometry || null;
         if (jordbundskortGeometry && typeof jordbundskortGeometry === 'string') {
            try {
               jordbundskortGeometry = JSON.parse(jordbundskortGeometry);
            } catch {
               jordbundskortGeometry = null;
            }
         }

         const jordbundskortOverlap = source.jordbundskortOverlap || null;
         const jordbundskortCalculatedAt = this.toEpochMilliseconds(source.jordbundskortCalculatedAt);


         return {
            bfe_nr: this.toStringOrNull(source.bfenummer) || "",
            documentID: rowDocumentId,
            dokumentId: deedDocumentId,
            matchedBfeNr,
            area: area,
            originalTotalPrice: originalTotalPrice,
            totalPrice: indexedLandPrice,
            transactionDate: new Date(source.dato || ""),
            pricePerSquareMeter: adjustedPricePerSquareMeter,
            originalPricePerSquareMeter: originalPricePerSquareMeter,
            priceIndexSource: normalizedPriceIndexSource,
            indexDifferencePct,
            coordinates: {
               lat: source.geopoint ? source.geopoint[1] : 0,
               lng: source.geopoint ? source.geopoint[0] : 0
            },
            geoshape: source.geoshape || null,
            address: "",
            transactionType: source.type || "",
            transactionStatus,
            landbrugsnotering,
            error: source.error || 0,
            reasonCode,
            reasonText,
            prisDriftsbygning: rawDriftsbygning,
            prisStuehus: rawStuehus,
            prisDriftsbygningIndexed: indexedDriftsbygning,
            prisStuehusIndexed: indexedStuehus,
            overlapGeometry: {
               farmlandGeometry: markOverlapGeometry,
               jordbundskortGeometry: jordbundskortGeometry
            },
            overlapArea: {
               markOverlap: markOverlapPct,
               markOverlapLayerYear,
               markOverlapCalculatedAt: markOverlapCalculatedAt > 0 ? markOverlapCalculatedAt : null,
               jordbundskortOverlap: jordbundskortOverlap,
               jordbundskortCalculatedAt: jordbundskortCalculatedAt > 0 ? jordbundskortCalculatedAt : null
            }
         };
      }).filter(isValidtJPBReferenceProperty);

      return validProperties;
   }

   /**
    * Point features at the same coordinates as the main JPB layer, carrying only
    * properties needed for the hover / "original" text layer. Updated via setData
    * when checks or assumptions change, without rebuilding the main point GeoJSON.
    */
   public static buildJPBHoverLabelsGeoJSON(
      properties: JPBReferenceProperty[],
      context: {
         checkedItems: Set<string>;
         assumptions?: Partial<JPBAssumptions>;
         areaGrouping: AreaGrouping;
         priceMode: PriceMode;
      }
   ): { type: "FeatureCollection"; features: any[] } {
      const resolvedAssumptions = getResolvedJPBAssumptions(context.assumptions);
      return {
         type: "FeatureCollection",
         features: properties.map((prop) => {
            const lockState = this.getSelectionLockState(prop, resolvedAssumptions);
            const hasIssue = lockState.source === "issue";
            const mapHoverDetail =
               this.getJPBExclusionExplanation(prop, {
                  checkedItems: context.checkedItems,
                  assumptions: resolvedAssumptions,
                  areaGrouping: context.areaGrouping,
                  priceMode: context.priceMode
               }) ?? "";

            const formattedOriginalM2 = prop.originalPricePerSquareMeter
               ? formatPricePerSquareMeter(prop.originalPricePerSquareMeter, AreaUnit.SquareMeter, 2, false)
               : null;
            const formattedOriginalHa = prop.originalPricePerSquareMeter
               ? formatPricePerSquareMeter(prop.originalPricePerSquareMeter, AreaUnit.Hectare, 0, false)
               : null;

            return {
               id: prop.documentID,
               type: "Feature",
               geometry: {
                  type: "Point",
                  coordinates: [prop.coordinates.lng, prop.coordinates.lat]
               },
               properties: {
                  documentID: prop.documentID,
                  hasIssue,
                  issueText: lockState.issueText,
                  mapHoverDetail,
                  transactionDateFormatted: formatDate(prop.transactionDate),
                  formattedOriginalM2,
                  formattedOriginalHa
               }
            };
         })
      };
   }

   public static convertToGeoJSON(
      properties: JPBReferenceProperty[],
      context: {
         checkedItems: Set<string>;
         assumptions?: Partial<JPBAssumptions>;
         priceMode: PriceMode;
      }
   ): any {
      const resolvedAssumptions = getResolvedJPBAssumptions(context.assumptions);
      return {
         type: "FeatureCollection",
         features: properties.map((prop) => {
            const effectiveMarkPrice = calculateEffectiveMarkSquareMeterPrice(prop);

            const hasBuildingValue =
               (typeof prop.prisDriftsbygning === "number" && Number.isFinite(prop.prisDriftsbygning) && prop.prisDriftsbygning > 0) ||
               (typeof prop.prisStuehus === "number" && Number.isFinite(prop.prisStuehus) && prop.prisStuehus > 0);
            const lockState = this.getSelectionLockState(prop, resolvedAssumptions);
            const buildingLocked = lockState.source === "building";
            const hasIssue = lockState.source === "issue";
            const selectionLocked = lockState.isLocked;
            const transactionTypeIncluded =
               resolvedAssumptions.includeTransactionTypes.includes(prop.transactionType);
            const showPipelineIssueMarker = hasIssue && transactionTypeIncluded;
            const isChecked = context.checkedItems.has(prop.documentID);

            const formattedStandardM2 = prop.pricePerSquareMeter
               ? formatPricePerSquareMeter(prop.pricePerSquareMeter, AreaUnit.SquareMeter, 2, false)
               : null;
            const formattedStandardHa = prop.pricePerSquareMeter
               ? formatPricePerSquareMeter(prop.pricePerSquareMeter, AreaUnit.Hectare, 0, false)
               : null;
            const formattedLandbrugM2 = effectiveMarkPrice
               ? formatPricePerSquareMeter(effectiveMarkPrice, AreaUnit.SquareMeter, 2, false)
               : null;
            const formattedLandbrugHa = effectiveMarkPrice
               ? formatPricePerSquareMeter(effectiveMarkPrice, AreaUnit.Hectare, 0, false)
               : null;

            let buildingIconName: string | null = null;
            if (hasBuildingValue && !hasIssue) {
               if (buildingLocked) {
                  buildingIconName = 'jpb-house-locked';
               } else if (isChecked) {
                  buildingIconName = 'jpb-house-checked';
               } else {
                  buildingIconName = 'jpb-house-unchecked';
               }
            }

            return {
               id: prop.documentID,
               type: "Feature",
               geometry: {
                  type: "Point",
                  coordinates: [prop.coordinates.lng, prop.coordinates.lat]
               },
               properties: {
                  bfe_nr: prop.bfe_nr,
                  documentID: prop.documentID,
                  dokumentId: prop.dokumentId ?? null,
                  checked: isChecked,
                  pricePerSquareMeter: prop.pricePerSquareMeter,
                  originalPricePerSquareMeter: prop.originalPricePerSquareMeter,
                  effectiveMarkPrice: effectiveMarkPrice,
                  hasBuilding: hasBuildingValue,
                  buildingLocked: !!buildingLocked,
                  hasIssue,
                  showPipelineIssueMarker,
                  issueText: lockState.issueText,
                  selectionLocked,
                  buildingIconName: buildingIconName,
                  buildingIconSize: hasBuildingValue && !hasIssue ? 1.0 : null,
                  transactionDateFormatted: formatDate(prop.transactionDate),
                  formattedStandardM2: formattedStandardM2,
                  formattedStandardHa: formattedStandardHa,
                  formattedLandbrugM2: formattedLandbrugM2,
                  formattedLandbrugHa: formattedLandbrugHa
               }
            };
         })
      };
   }

   public static convertToGeojsonGeometry(properties: JPBReferenceProperty[], checkedItems: Set<string>): any {
      return {
         type: "FeatureCollection",
         features: properties.map((prop) => ({
            id: prop.documentID,
            type: "Feature",
            geometry: prop.geoshape,
            properties: {
               bfe_nr: prop.bfe_nr,
               documentID: prop.documentID,
               checked: checkedItems.has(prop.documentID),
               pricePerSquareMeter: prop.pricePerSquareMeter,
            }
         }))
      }
   }


   public static toCombinedSearchGeometry(bufferGeometry: any): any | null {
      if (!bufferGeometry) return null;

      const polygonFeatures: any[] = [];
      const collectPolygonFeatures = (input: any) => {
         if (!input) return;
         if (input.type === "FeatureCollection") {
            (input.features || []).forEach((feature: any) => collectPolygonFeatures(feature));
            return;
         }
         if (input.type === "Feature") {
            collectPolygonFeatures(input.geometry);
            return;
         }
         if (input.type === "GeometryCollection") {
            (input.geometries || []).forEach((geometry: any) => collectPolygonFeatures(geometry));
            return;
         }
         if (input.type === "Polygon" || input.type === "MultiPolygon") {
            polygonFeatures.push({
               type: "Feature",
               geometry: input,
               properties: {}
            });
         }
      };

      collectPolygonFeatures(bufferGeometry);

      if (!polygonFeatures.length) return null;
      if (polygonFeatures.length === 1) return polygonFeatures[0];

      try {
         const merged = union(featureCollection(polygonFeatures as any));
         if (merged) return merged;
      } catch {
         // Fallback handled below
      }

      const combined = combine(featureCollection(polygonFeatures as any));
      if (combined?.features?.length) return combined.features[0];
      return polygonFeatures[0];
   }

   public static extractPolygonCoordinates(bufferGeometry: any): number[][][] | null {
      if (!bufferGeometry) {
         return null;
      }

      const combinedGeometryFeature = this.toCombinedSearchGeometry(bufferGeometry);
      if (!combinedGeometryFeature?.geometry) return null;
      const geometry = combinedGeometryFeature.geometry;

      if (geometry.type === 'Polygon' && geometry.coordinates) {
         if (geometry.coordinates.length > 0 && geometry.coordinates[0]) {
            return [geometry.coordinates[0]];
         }
      }

      // Handle MultiPolygon - extract all polygons
      if (geometry.type === 'MultiPolygon' && geometry.coordinates) {
         const polygons: number[][][] = [];
         geometry.coordinates.forEach((polygon: number[][][]) => {
            if (polygon && polygon.length > 0 && polygon[0]) {
               polygons.push(polygon[0]);
            }
         });
         return polygons.length > 0 ? polygons : null;
      }

      return null;
   }




   private static getAgroMultiplierForPeriods(landsdel: string, purchaseKey: string, referenceKey: string): number | null {
      const region = this.getAgroRegionByLandsdel(landsdel);
      const values = region?.values;
      if (!values) return null;
      const salesIndex = values[purchaseKey as keyof typeof values];
      const compareIndex = values[referenceKey as keyof typeof values];
      const fallbackValue = Object.values(values).at(-1) as number | undefined;
      const finalSalesIndex = salesIndex ?? fallbackValue;
      const finalCompareIndex = compareIndex ?? fallbackValue;
      if (!finalSalesIndex || !finalCompareIndex) return null;
      if (finalSalesIndex === finalCompareIndex) return 1;
      return finalCompareIndex / finalSalesIndex;
   }

   private static getMolbakMultiplierForPeriods(landsdel: string, purchaseKey: string, referenceKey: string): number | null {
      const state = this.getIndexedLandsdelState();
      const doc = this.getMolbakDocumentForLandsdel(landsdel, state);
      if (!doc) return null;
      const factorRow = this.buildMolbakAppliedFactorRow(doc);
      const salesFactor = this.resolveFactorWithFallback(purchaseKey, factorRow.factorsByPeriod);
      const compareFactor = this.resolveFactorWithFallback(referenceKey, factorRow.factorsByPeriod);
      if (!salesFactor || !compareFactor) return null;
      if (salesFactor === compareFactor) return 1;
      return compareFactor / salesFactor;
   }

   public static calculatePresentValue(
      price: number,
      saleDate: Date,
      kommune: string,
      compareDate?: Date,
      sourceInput: JPBPriceIndexSource = JPBPriceIndexSource.AgroCura
   ) {
      const source = this.normalizePriceIndexSource(sourceInput);
      if (source === JPBPriceIndexSource.None) return price;

      const purchaseKey = this.parseQuarterKey(saleDate);
      if (!purchaseKey) return price;

      const referenceDate = compareDate || new Date();
      const referenceKey = this.parseQuarterKey(referenceDate);
      if (!referenceKey) return price;
      const effectivePurchaseKey = this.clampToLatestCompletedQuarter(purchaseKey);
      const effectiveReferenceKey = this.clampToLatestCompletedQuarter(referenceKey);
      if (effectivePurchaseKey === effectiveReferenceKey) return price;

      const landsdel = this.resolveLandsdelForKommune(kommune);
      const multiplier = source === JPBPriceIndexSource.Molbak
         ? this.getMolbakMultiplierForPeriods(landsdel, effectivePurchaseKey, effectiveReferenceKey)
         : this.getAgroMultiplierForPeriods(landsdel, effectivePurchaseKey, effectiveReferenceKey);
      if (!multiplier || !Number.isFinite(multiplier) || multiplier <= 0) return price;
      return price * multiplier;
   }
}

function isValidtJPBReferenceProperty(obj: { [key: string]: unknown }): obj is JPBReferenceProperty {
   return (
      obj &&
      typeof obj.documentID === 'string' &&
      (obj.dokumentId === undefined || obj.dokumentId === null || typeof obj.dokumentId === 'string') &&
      typeof obj.bfe_nr === 'string' &&
      (obj.matchedBfeNr === undefined || obj.matchedBfeNr === null || typeof obj.matchedBfeNr === 'string') &&
      typeof obj.error === 'number' &&
      typeof obj.area === 'number' &&
      typeof obj.originalTotalPrice === 'number' &&
      typeof obj.totalPrice === 'number' &&
      obj.transactionDate instanceof Date &&
      typeof obj.pricePerSquareMeter === 'number' &&
      typeof obj.originalPricePerSquareMeter === 'number' &&
      typeof obj.coordinates === 'object' &&
      typeof obj.address === 'string' &&
      typeof obj.transactionType === 'string' &&
      typeof obj.transactionStatus === 'string' &&
      (obj.landbrugsnotering === undefined || obj.landbrugsnotering === null || typeof obj.landbrugsnotering === 'string') &&
      (obj.reasonCode === undefined || obj.reasonCode === null || typeof obj.reasonCode === 'string') &&
      (obj.reasonText === undefined || obj.reasonText === null || typeof obj.reasonText === 'string')
   )
}